import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogOut, Send, ClipboardCheck } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const YesNoCell = ({ value, onChange, testid }) => (
  <div className="flex gap-2">
    {['yes', 'no', 'na'].map((opt) => {
      const labels = { yes: 'Yes', no: 'No', na: 'N/A' };
      const colorActive = {
        yes: 'bg-green-600 border-green-600 text-white',
        no: 'bg-red-600 border-red-600 text-white',
        na: 'bg-gray-500 border-gray-500 text-white',
      };
      const isActive = value === opt;
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`px-3 py-1 text-xs font-semibold rounded border-2 transition-all ${isActive ? colorActive[opt] : 'bg-white border-gray-300 text-gray-700 hover:border-gray-500'}`}
          data-testid={`${testid}-${opt}`}
        >
          {labels[opt]}
        </button>
      );
    })}
  </div>
);

export const OperatorChecklist = () => {
  const navigate = useNavigate();
  const [operator, setOperator] = useState(null);
  const [sections, setSections] = useState([]);
  const [lorries, setLorries] = useState([]);
  const [lorryId, setLorryId] = useState('');
  const [vehicleRegNo, setVehicleRegNo] = useState('');
  const [liftingMachineNo, setLiftingMachineNo] = useState('');
  const [supervisorName, setSupervisorName] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [equipmentType, setEquipmentType] = useState('Lorry Crane');
  const [equipmentTypes, setEquipmentTypes] = useState(['Lorry Crane', 'Tailgate Lorry']);
  const [answers, setAnswers] = useState({}); // { item_no: { status, remarks, item_text } }
  const [overallNotes, setOverallNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const sigRef = useRef(null);
  const supSigRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem('njk_operator');
    if (!stored) {
      navigate('/operator/login');
      return;
    }
    setOperator(JSON.parse(stored));

    axios.get(`${BACKEND_URL}/api/maintenance-checklist/template`)
      .then((r) => setSections(r.data.categories || []))
      .catch((e) => toast.error(getErrorMessage(e, 'Failed to load checklist')));

    axios.get(`${BACKEND_URL}/api/maintenance-checklist/equipment-types`)
      .then((r) => setEquipmentTypes(r.data.types || []))
      .catch(() => setEquipmentTypes(['Lorry Crane','Tailgate Lorry']));

    axios.get(`${BACKEND_URL}/api/lorries?status=active`)
      .then((r) => setLorries(r.data || []))
      .catch((e) => toast.error(getErrorMessage(e, 'Failed to load lorries')));
  }, [navigate]);

  const setAnswer = (item_no, item_text, field, value) => {
    setAnswers((prev) => ({
      ...prev,
      [item_no]: { ...(prev[item_no] || {}), item_text, [field]: value },
    }));
  };

  const onLorrySelect = (id) => {
    setLorryId(id);
    const l = lorries.find((x) => x.id === id);
    if (l) setVehicleRegNo(l.plate_no || '');
  };

  const logout = () => {
    localStorage.removeItem('njk_operator');
    navigate('/operator/login');
  };

  // Count total items (including sub-items)
  const flatItems = [];
  sections.forEach((sec) => {
    sec.items.forEach((it) => {
      if (it.subs && it.subs.length > 0) {
        flatItems.push({ no: it.no, text: it.text, isHeader: true });
        it.subs.forEach((s) => {
          // composite no like "14a"
          const compositeNo = `${it.no}${s.no.replace(/[()]/g, '')}`;
          flatItems.push({ no: compositeNo, text: `${s.no} ${s.text}`, parent: it.no });
        });
      } else {
        flatItems.push({ no: it.no, text: it.text });
      }
    });
  });

  const totalItems = flatItems.length;
  const answered = flatItems.filter((it) => answers[it.no]?.status).length;

  const submit = async () => {
    if (!lorryId) { toast.error('Please select a lorry first'); return; }
    if (!vehicleRegNo) { toast.error('Vehicle Registration Number is required'); return; }
    // Lifting Machine Number is only required for Lorry Crane (not Tailgate Lorry)
    if (equipmentType !== 'Tailgate Lorry' && !liftingMachineNo) {
      toast.error('Lifting Machine Number is required for Lorry Crane');
      return;
    }

    const items = [];
    let missing = 0;
    flatItems.forEach((it) => {
      const a = answers[it.no];
      if (!a?.status) { missing += 1; return; }
      items.push({
        item_no: it.no,
        item_text: it.text,
        status: a.status,
        remarks: a.remarks || '',
      });
    });
    if (missing > 0) { toast.error(`Please mark all ${missing} remaining item(s)`); return; }

    const operatorSig = sigRef.current && !sigRef.current.isEmpty() ? sigRef.current.toDataURL() : '';
    const supervisorSig = supSigRef.current && !supSigRef.current.isEmpty() ? supSigRef.current.toDataURL() : '';

    setSubmitting(true);
    try {
      const lorry = lorries.find((l) => l.id === lorryId);
      const payload = {
        operator_id: operator.id,
        operator_name: operator.full_name,
        operator_phone_last4: '****',
        lorry_id: lorryId,
        lorry_plate: lorry?.plate_no || '',
        vehicle_reg_no: vehicleRegNo,
        lifting_machine_no: liftingMachineNo,
        supervisor_name: supervisorName,
        supervisor_signature: supervisorSig,
        frequency,
        equipment_type: equipmentType,
        items,
        overall_notes: overallNotes,
        signature: operatorSig,
      };
      const res = await axios.post(`${BACKEND_URL}/api/maintenance-checklist`, payload);
      toast.success(`✅ ${res.data.checklist_no} submitted — ${res.data.defect_count} defect(s). Admin notified.`);
      setAnswers({}); setOverallNotes(''); setLorryId(''); setVehicleRegNo('');
      setLiftingMachineNo(''); setSupervisorName('');
      sigRef.current?.clear(); supSigRef.current?.clear();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Submission failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!operator) return null;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="bg-gradient-to-r from-red-900 via-red-800 to-amber-900 text-white shadow-lg sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white rounded p-1">
                <img src="/logo.png" alt="NJK" className="h-10 w-10 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
              <div>
                <p className="font-bold text-base leading-tight">NJK LOGISTICS PTE LTD</p>
                <p className="text-xs opacity-90">Sample Checklist for Lorry Crane</p>
                <p className="text-xs opacity-75">Operator: {operator.full_name}</p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={logout} className="text-slate-900" data-testid="logout-btn">
              <LogOut size={14} className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <Card className="mb-4 border-2 border-gray-400">
          <CardContent className="p-4 text-xs text-gray-700">
            <strong>Note:</strong> This checklist provides the basic requirements for safe operations of a lorry crane.
            Lorry crane users should consult manufacturers, suppliers and owners on operations and safety requirements
            and address any concerns before using the equipment. This checklist is non-exhaustive and users are
            recommended to make the necessary customisation to suit your work processes and conditions at the workplace.
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardHeader><CardTitle>Lorry & Frequency</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Equipment Type *</Label>
              <Select value={equipmentType} onValueChange={setEquipmentType}>
                <SelectTrigger data-testid="equipment-type-select"><SelectValue placeholder="-- Choose --" /></SelectTrigger>
                <SelectContent>
                  {equipmentTypes.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Lorry *</Label>
              <Select value={lorryId} onValueChange={onLorrySelect}>
                <SelectTrigger data-testid="lorry-select"><SelectValue placeholder="-- Choose lorry --" /></SelectTrigger>
                <SelectContent>
                  {lorries.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.plate_no} — {l.description}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Frequency *</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger data-testid="frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily Pre-Operation</SelectItem>
                  <SelectItem value="weekly">Weekly Inspection</SelectItem>
                  <SelectItem value="monthly">Monthly Inspection</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vehicle Registration Number *</Label>
              <Input value={vehicleRegNo} onChange={(e) => setVehicleRegNo(e.target.value.toUpperCase())}
                     placeholder="e.g. SHA1234A" data-testid="vehicle-reg-no" />
            </div>
            <div>
              <Label>Lifting Machine Number *</Label>
              <Input value={liftingMachineNo} onChange={(e) => setLiftingMachineNo(e.target.value)}
                     placeholder="e.g. LM:123456x" data-testid="lifting-machine-no" />
            </div>
            <div>
              <Label>Lifting Supervisor Name</Label>
              <Input value={supervisorName} onChange={(e) => setSupervisorName(e.target.value)}
                     placeholder="Supervisor name (optional)" data-testid="supervisor-name" />
            </div>
          </CardContent>
        </Card>

        <div className="mb-3 text-sm text-gray-600 sticky top-16 bg-gray-50 py-2 z-30">
          Progress: <strong>{answered}/{totalItems}</strong> items
          <div className="h-2 bg-gray-200 rounded-full mt-1 overflow-hidden">
            <div className="h-full bg-blue-600 transition-all" style={{ width: `${totalItems ? (answered/totalItems)*100 : 0}%` }} />
          </div>
        </div>

        {sections.map((sec) => (
          <Card key={sec.section} className="mb-4">
            <CardHeader className="bg-gray-200 py-2">
              <CardTitle className="text-base">{sec.section}. {sec.title}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {sec.items.map((it) => {
                  const hasSubs = it.subs && it.subs.length > 0;
                  const ans = answers[it.no] || {};
                  return (
                    <div key={it.no}>
                      <div className={`flex items-start gap-3 p-3 ${ans.status === 'no' ? 'bg-red-50' : ''}`}>
                        <span className="font-bold text-sm w-8 flex-shrink-0">{it.no}</span>
                        <div className="flex-1 text-sm">{it.text}</div>
                        <div className="flex-shrink-0">
                          <YesNoCell
                            value={ans.status}
                            onChange={(v) => setAnswer(it.no, it.text, 'status', v)}
                            testid={`status-${it.no}`}
                          />
                        </div>
                      </div>
                      {ans.status === 'no' && !hasSubs && (
                        <div className="px-3 pb-2">
                          <Input placeholder="Remarks (required for No)"
                                 value={ans.remarks || ''}
                                 onChange={(e) => setAnswer(it.no, it.text, 'remarks', e.target.value)}
                                 className="text-sm" data-testid={`remarks-${it.no}`} />
                        </div>
                      )}
                      {hasSubs && (
                        <div className="divide-y border-t bg-gray-50">
                          {it.subs.map((s) => {
                            const compositeNo = `${it.no}${s.no.replace(/[()]/g, '')}`;
                            const subAns = answers[compositeNo] || {};
                            return (
                              <div key={compositeNo} className={subAns.status === 'no' ? 'bg-red-50' : ''}>
                                <div className="flex items-start gap-3 p-3 pl-12">
                                  <span className="text-xs font-medium w-8 flex-shrink-0">{s.no}</span>
                                  <div className="flex-1 text-sm">{s.text}</div>
                                  <YesNoCell
                                    value={subAns.status}
                                    onChange={(v) => setAnswer(compositeNo, `${s.no} ${s.text}`, 'status', v)}
                                    testid={`status-${compositeNo}`}
                                  />
                                </div>
                                {subAns.status === 'no' && (
                                  <div className="px-3 pb-2 pl-12">
                                    <Input placeholder="Remarks (required for No)"
                                           value={subAns.remarks || ''}
                                           onChange={(e) => setAnswer(compositeNo, `${s.no} ${s.text}`, 'remarks', e.target.value)}
                                           className="text-sm" />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}

        <Card className="mb-4">
          <CardHeader><CardTitle>Overall Notes</CardTitle></CardHeader>
          <CardContent>
            <Textarea value={overallNotes} onChange={(e) => setOverallNotes(e.target.value)}
                      rows={3} placeholder="Any other observations" />
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardHeader><CardTitle>Signatures</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="block mb-1">Inspected by: Crane Operator</Label>
              <p className="text-xs text-gray-500 mb-1">{operator.full_name}</p>
              <div className="border border-gray-300 rounded bg-white">
                <SignatureCanvas ref={sigRef} penColor="black"
                                 canvasProps={{ width: 400, height: 120, className: 'w-full' }} />
              </div>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => sigRef.current?.clear()}>Clear</Button>
            </div>
            <div>
              <Label className="block mb-1">Supervised by: Lifting Supervisor</Label>
              <p className="text-xs text-gray-500 mb-1">{supervisorName || '(supervisor name above)'}</p>
              <div className="border border-gray-300 rounded bg-white">
                <SignatureCanvas ref={supSigRef} penColor="black"
                                 canvasProps={{ width: 400, height: 120, className: 'w-full' }} />
              </div>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => supSigRef.current?.clear()}>Clear</Button>
            </div>
          </CardContent>
        </Card>

        <div className="sticky bottom-0 bg-gradient-to-t from-gray-50 via-gray-50 pt-3 pb-2 -mx-4 px-4">
          <Button onClick={submit} disabled={submitting} className="w-full" size="lg" data-testid="submit-checklist">
            <Send size={18} className="mr-2" />
            {submitting ? 'Submitting...' : 'Submit Checklist'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OperatorChecklist;
